/**
 * Carrying an evaluated dynamic component into the viewport.
 *
 * The graph speaks SketchUp: inches, Z up. The baked GLB speaks glTF: metres,
 * Y up, with the importer writing every vertex as (x, z, -y) * 0.0254. So a
 * transform computed on the graph has to be re-expressed in that basis before
 * a mesh can be moved by it.
 *
 * What each part gets is a DELTA — where it should be now against where the
 * SKP baked it — because the geometry in the GLB is already in its imported
 * world position. At the imported size every delta is the identity, so placing
 * a module never nudges it.
 */

import type { CatalogItem, SceneElement } from "@calcplan/domain";
import {
  evaluateDynamicComponents,
  evaluateFormula,
  MILLIMETERS_PER_INCH,
  parseFormula,
  type DynamicAttribute,
  type DynamicComponentGraph,
  type DynamicEvaluation,
  type DynamicNode,
  type EvaluatedPart,
  type FormulaContext,
  type FormulaNode,
  type FormulaValue,
} from "@calcplan/dynamic-components";
import { planInteraction } from "./component-interaction";

const INCH_TO_METRE = 0.0254;

/** Below this the base placement cannot be inverted, so the part stays put. */
const SINGULAR_DETERMINANT = 1e-12;

export function identity4(): number[] {
  return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
}

/**
 * Where a part must move, expressed in the GLB's own space and ready to be fed
 * straight to a Babylon node sitting between the loaded model and its mesh.
 */
export function partDeltaMatrix(
  part: EvaluatedPart,
  bounds?: DynamicPartBounds,
  inheritedRootResize?: number[],
): number[] {
  const base = skpToGlb(part.baseWorldMatrix);
  const inverse = invertAffine(base);
  if (!inverse) return identity4();
  const target = preservePhysicalMargins(part, base, skpToGlb(part.worldMatrix), bounds);
  const ownDelta = multiply4(target, inverse);
  const delta = inheritedRootResize
    ? multiply4(inheritedRootResize, ownDelta)
    : ownDelta;
  return isRunawayDelta(delta) ? identity4() : delta;
}

/**
 * Branches that are baked into a resizable countertop opening.
 *
 * Imported sculpted sinks contain two coincident top-level branches: the
 * opening/finish (currently named `aca`) and the basin below it. Both use the
 * same X/Y/LenX/LenY formulas. The countertop itself is one baked mesh, so its
 * opening follows the root stretch; these branches must inherit that same
 * stretch or the basin stays behind and crosses the opening after a resize.
 *
 * Matching the shared formula signature keeps this valid for all three
 * catalogue variants and for future imports with the same component logic,
 * without depending on asset IDs or on the companion branch's display name.
 */
export function rootResizeFollowerPaths(graph: DynamicComponentGraph): Set<string> {
  const paths = new Set<string>();
  const storedMillimetres = (attribute: DynamicAttribute | undefined): number | null => {
    const numeric = typeof attribute?.value === "number"
      ? attribute.value
      : Number(attribute?.value);
    return Number.isFinite(numeric) ? numeric * MILLIMETERS_PER_INCH : null;
  };
  const isStaticBakedCountertop = (node: DynamicNode): boolean => {
    const name = normalizeNodeName(node.name);
    const width = storedMillimetres(node.attributes.lenx);
    const depth = storedMillimetres(node.attributes.leny);
    return (name === "chapa pedra" || name === "chapa de pedra")
      && node.attributes.lenx?.formula === undefined
      && node.attributes.leny?.formula === undefined
      && width !== null
      && depth !== null
      && Math.abs(width - graph.baseDimensionsMm.width) < 1
      && Math.abs(depth - graph.baseDimensionsMm.depth) < 1;
  };
  const layoutSignature = (node: DynamicNode): string | null => {
    const formulas = (["x", "y", "lenx", "leny"] as const).map((attribute) => {
      const formula = node.attributes[attribute]?.formula;
      return typeof formula === "string"
        ? formula.replace(/\s/g, "").toLocaleLowerCase("en-US")
        : null;
    });
    return formulas.every((formula): formula is string => formula !== null)
      ? formulas.join("|")
      : null;
  };

  const visit = (parent: DynamicNode): void => {
    const sinks = parent.children.filter((child) => (
      normalizeNodeName(child.name).includes("cuba esculpida")
    ));
    for (const sink of sinks) {
      const signature = layoutSignature(sink);
      if (!signature) continue;
      for (const sibling of parent.children) {
        if (layoutSignature(sibling) === signature) paths.add(sibling.path);
      }
    }
    /*
     * A bancada engrossada traz a placa superior assada no tamanho original,
     * sem fórmula LenX/LenY. Ela é a própria malha que contém o recorte; por
     * isso acompanha a raiz junto da cuba, enquanto as saias ao redor continuam
     * obedecendo às próprias fórmulas.
     */
    if (parent === graph.root && sinks.length) {
      for (const sibling of parent.children) {
        if (isStaticBakedCountertop(sibling)) paths.add(sibling.path);
      }
    }
    for (const child of parent.children) visit(child);
  };
  visit(graph.root);
  return paths;
}

export function pathFollowsRootResize(
  path: string,
  followerRoots: ReadonlySet<string>,
): boolean {
  for (const root of followerRoots) {
    if (path === root || path.startsWith(`${root} / `)) return true;
  }
  return false;
}

/**
 * SketchUp sizes a component by its declared LenX/Y/Z, but the baked geometry
 * is not required to occupy that whole interval. Cabinet panels commonly keep
 * a fixed 15 mm inset: a 608.5 mm component contains 593.5 mm of MDF. Scaling
 * the vertices by `next / 608.5` also scales that inset, opening a visible gap
 * at the back. Add the declared absolute change to the measured geometry span
 * instead, preserving both the formula and the author's fixed construction.
 */
function preservePhysicalMargins(
  part: EvaluatedPart,
  base: number[],
  target: number[],
  bounds?: DynamicPartBounds,
): number[] {
  const physicalSize = bounds?.localSize;
  if (!physicalSize) return target;
  const inverseBase = invertAffine(base);
  if (!inverseBase) return target;
  const localDelta = multiply4(inverseBase, target);
  const axisName = ["x", "z", "y"] as const; // glTF local axes -> SketchUp axes
  const adjustment: [number, number, number] = [1, 1, 1];
  const MIN_INHERITED_COVERAGE = 0.65;
  const MAX_INHERITED_COVERAGE = 1.5;

  for (let axis = 0; axis < 3; axis += 1) {
    const column = axis * 4;
    const rawScale = Math.hypot(
      localDelta[column]!,
      localDelta[column + 1]!,
      localDelta[column + 2]!,
    );
    const name = axisName[axis]!;
    const anchorMm = part.anchorSizeMm[name];
    const currentMm = part.sizeMm[name];
    const stretchDeltaMm = part.stretchDeltaMm[name];
    const spanMetres = physicalSize[axis]!;
    if (
      !Number.isFinite(rawScale)
      || !Number.isFinite(anchorMm)
      || !Number.isFinite(currentMm)
      || !Number.isFinite(stretchDeltaMm)
      || !Number.isFinite(spanMetres)
      || spanMetres < 1e-9
    ) continue;

    const declaredScale = Math.abs(anchorMm) > 1e-6 ? currentMm / anchorMm : Number.NaN;
    const scaleTolerance = Math.max(1, Math.abs(rawScale), Math.abs(declaredScale)) * 1e-7;
    const ownsScale = Number.isFinite(declaredScale)
      && Math.abs(rawScale - declaredScale) <= scaleTolerance;

    if (!ownsScale) {
      // Edge banding and other passive descendants inherit their panel's
      // ratio while keeping their own Len untouched. Correct them only when
      // their geometry covers essentially that same declared interval. A
      // small fitting inside a cabinet must remain proportionally scaled, not
      // become metres long merely because its container did.
      if (Math.abs(rawScale - 1) < 1e-9 || Math.abs(stretchDeltaMm) < 1e-6) continue;
      const inheritedReferenceMetres = Math.abs((stretchDeltaMm / 1_000) / (rawScale - 1));
      const coverage = spanMetres / inheritedReferenceMetres;
      if (
        !Number.isFinite(coverage)
        || coverage < MIN_INHERITED_COVERAGE
        || coverage > MAX_INHERITED_COVERAGE
      ) continue;
    }

    const correctedScale = 1 + (stretchDeltaMm / 1_000) / spanMetres;
    if (!Number.isFinite(correctedScale) || correctedScale <= 1e-9 || rawScale <= 1e-9) continue;
    adjustment[axis] = correctedScale / rawScale;
  }

  if (adjustment.every((value) => Math.abs(value - 1) <= 1e-12)) return target;
  return multiply4(target, scale4(adjustment));
}

function scale4([x, y, z]: [number, number, number]): number[] {
  return [x, 0, 0, 0, 0, y, 0, 0, 0, 0, z, 0, 0, 0, 0, 1];
}

/**
 * Guards against a part being blown up instead of resized.
 *
 * The delta divides the new placement by the baked one, so a part whose baked
 * geometry is nearly flat on some axis — edge banding is modelled exactly that
 * way — turns a small disagreement into an enormous factor. Asking "Gaveteiro
 * Teste" for 20 mm of depth produced a 200x stretch that drew a 6 mm strip as
 * 1,361 m of panel across the viewport. Nothing legitimate comes close: the
 * widest real stretch in the tenant's catalogue is under 6x.
 */
const MAX_PART_STRETCH = 50;

function isRunawayDelta(delta: number[]): boolean {
  for (let column = 0; column < 3; column += 1) {
    const length = Math.hypot(
      delta[column * 4]!,
      delta[column * 4 + 1]!,
      delta[column * 4 + 2]!,
    );
    if (!Number.isFinite(length) || length > MAX_PART_STRETCH) return true;
  }
  for (let axis = 12; axis < 15; axis += 1) {
    if (!Number.isFinite(delta[axis]!)) return true;
  }
  return false;
}

/**
 * Rebases a SketchUp transform onto the glTF axes: (x, y, z) becomes
 * (x, z, -y), and the translation converts from inches to metres.
 *
 * With B the change of basis, the transform becomes B·M·B⁻¹. B is a rotation,
 * so B⁻¹ is its transpose and the whole thing reduces to swapping rows and
 * columns 1 and 2 with a sign flip — written out here rather than looped so
 * the axis convention stays readable.
 */
export function skpToGlb(matrix: number[]): number[] {
  const m = (row: number, column: number) => matrix[column * 4 + row] ?? 0;
  // Linear part: rows and columns permuted by y -> z, z -> -y.
  const linear = [
    [m(0, 0), m(0, 2), -m(0, 1)],
    [m(2, 0), m(2, 2), -m(2, 1)],
    [-m(1, 0), -m(1, 2), m(1, 1)],
  ];
  return [
    linear[0]![0]!, linear[1]![0]!, linear[2]![0]!, 0,
    linear[0]![1]!, linear[1]![1]!, linear[2]![1]!, 0,
    linear[0]![2]!, linear[1]![2]!, linear[2]![2]!, 0,
    m(0, 3) * INCH_TO_METRE, m(2, 3) * INCH_TO_METRE, -m(1, 3) * INCH_TO_METRE, 1,
  ];
}

/** Column-major 4x4 product. */
export function multiply4(left: number[], right: number[]): number[] {
  const result = new Array<number>(16).fill(0);
  for (let column = 0; column < 4; column += 1) {
    for (let row = 0; row < 4; row += 1) {
      let total = 0;
      for (let k = 0; k < 4; k += 1) total += left[k * 4 + row]! * right[column * 4 + k]!;
      result[column * 4 + row] = total;
    }
  }
  return result;
}

/**
 * Inverts a transform whose last row is (0,0,0,1), or returns null when its
 * axes are degenerate. A part baked with a zero-thickness axis — edge banding
 * is modelled that way — cannot be inverted, and must simply not move.
 */
export function invertAffine(matrix: number[]): number[] | null {
  const a = matrix;
  const [m00, m10, m20] = [a[0]!, a[1]!, a[2]!];
  const [m01, m11, m21] = [a[4]!, a[5]!, a[6]!];
  const [m02, m12, m22] = [a[8]!, a[9]!, a[10]!];
  const determinant =
    m00 * (m11 * m22 - m12 * m21)
    - m01 * (m10 * m22 - m12 * m20)
    + m02 * (m10 * m21 - m11 * m20);
  if (!Number.isFinite(determinant) || Math.abs(determinant) < SINGULAR_DETERMINANT) return null;
  const inverseDeterminant = 1 / determinant;
  const i00 = (m11 * m22 - m12 * m21) * inverseDeterminant;
  const i01 = (m02 * m21 - m01 * m22) * inverseDeterminant;
  const i02 = (m01 * m12 - m02 * m11) * inverseDeterminant;
  const i10 = (m12 * m20 - m10 * m22) * inverseDeterminant;
  const i11 = (m00 * m22 - m02 * m20) * inverseDeterminant;
  const i12 = (m02 * m10 - m00 * m12) * inverseDeterminant;
  const i20 = (m10 * m21 - m11 * m20) * inverseDeterminant;
  const i21 = (m01 * m20 - m00 * m21) * inverseDeterminant;
  const i22 = (m00 * m11 - m01 * m10) * inverseDeterminant;
  const [tx, ty, tz] = [a[12]!, a[13]!, a[14]!];
  return [
    i00, i10, i20, 0,
    i01, i11, i21, 0,
    i02, i12, i22, 0,
    -(i00 * tx + i01 * ty + i02 * tz),
    -(i10 * tx + i11 * ty + i12 * tz),
    -(i20 * tx + i21 * ty + i22 * tz),
    1,
  ];
}

/**
 * The editor measures a module by its bounding box, the graph by the component's
 * declared LenX/LenY/LenZ, and the two differ whenever something overhangs — the
 * doors of "Balcão 3 portas" stand 15 mm proud of a carcass 600 mm deep, so the
 * box reads 615. Feeding the box straight in would deepen the carcass by that
 * overhang on every resize, so the constant is subtracted first.
 */
export function resolveDynamicRequest(
  graph: DynamicComponentGraph,
  dimensionsMm: { width: number; height: number; depth: number },
): { widthMm: number; heightMm: number; depthMm: number } {
  const declared = declaredLengthsMm(graph);
  return {
    widthMm: dimensionsMm.width - (graph.baseDimensionsMm.width - declared.width),
    heightMm: dimensionsMm.height - (graph.baseDimensionsMm.height - declared.height),
    depthMm: dimensionsMm.depth - (graph.baseDimensionsMm.depth - declared.depth),
  };
}

/**
 * What the module actually becomes when asked for a size.
 *
 * A dynamic component may refuse: "Estrutura Dinâmica" caps its own length at
 * `IF(ACOMP2 > ATR, ATR, ACOMP2)`, so dragging to 9 m leaves it at 6,06 m. The
 * caller needs that answer before it commits a size, otherwise the selection
 * box runs on alone and the module floats inside three metres of nothing.
 *
 * Evaluations are memoised because the resize path asks the same question twice
 * per frame — once to clamp the drag, once to place the parts.
 */
const evaluationCache = new Map<string, DynamicEvaluation>();
const EVALUATION_CACHE_LIMIT = 8;

/**
 * O que o operador mudou à mão num atributo do componente, por instância.
 *
 * A chave é `caminho do nó` + `/` + `nome do atributo`, que é como a cena grava
 * em `instanceOverrides` — um endereço estável, que sobrevive a renomear a peça
 * e a redimensionar o módulo.
 */
export type DynamicAttributeOverrides = Readonly<Record<string, string | number>>;

export function overrideKey(path: string, attribute: string): string {
  return `${path}/${attribute}`;
}

/**
 * O grafo com os valores editados no lugar dos importados.
 *
 * A sobrescrita entra como valor **e** apaga a fórmula do atributo: um número
 * digitado à mão que continuasse com a fórmula do arquivo seria recalculado por
 * cima na avaliação seguinte, e a edição pareceria não ter pegado.
 *
 * O pacote de componentes dinâmicos não sabe de sobrescritas — e não deve saber,
 * porque é consumido e não alterado. A substituição acontece aqui, sobre uma
 * cópia do grafo.
 */
export function withAttributeOverrides(
  graph: DynamicComponentGraph,
  overrides: DynamicAttributeOverrides,
): DynamicComponentGraph {
  const entries = Object.entries(overrides);
  if (!entries.length) return graph;

  const byPath = new Map<string, Map<string, string | number>>();
  for (const [key, value] of entries) {
    const cut = key.lastIndexOf("/");
    if (cut <= 0) continue;
    const path = key.slice(0, cut);
    const attribute = key.slice(cut + 1).toLowerCase();
    const bucket = byPath.get(path) ?? new Map<string, string | number>();
    bucket.set(attribute, value);
    byPath.set(path, bucket);
  }
  if (!byPath.size) return graph;

  const rewrite = (node: DynamicNode): DynamicNode => {
    const wanted = byPath.get(node.path);
    const children = node.children.map(rewrite);
    if (!wanted) return children === node.children ? node : { ...node, children };
    const attributes: Record<string, DynamicAttribute> = { ...node.attributes };
    for (const [attribute, value] of wanted) {
      /*
       * O nome é procurado sem distinguir maiúsculas.
       *
       * O grafo guarda o atributo com a grafia do arquivo — `Ab_Gav` — e a
       * edição chega em minúsculas. Gravando pela grafia recebida, nascia uma
       * segunda entrada `ab_gav` ao lado da original, e as fórmulas continuavam
       * lendo a de cima: a gaveta não abria por mais que o valor fosse gravado.
       */
      const existing = Object.keys(attributes)
        .find((name) => name.toLowerCase() === attribute) ?? attribute;
      const previous = attributes[existing];
      // Estados de interação frequentemente vêm do SKP como "0"/"1" sem
      // unidade declarada. Trocar "0" pelo número 1 faria o avaliador inferir
      // a unidade geral do componente (cm, por exemplo) e ler o estado como
      // 2,54; `IF(AB=1,...)` nunca abriria. Preservar o tipo textual mantém o
      // atributo adimensional tal como foi importado.
      const storedValue = typeof previous?.value === "string" && typeof value === "number"
        ? String(value)
        : value;
      attributes[existing] = {
        ...(previous ?? {}),
        value: storedValue,
        formula: undefined,
      };
    }
    return { ...node, attributes, children };
  };

  return { ...graph, root: rewrite(graph.root) };
}

/**
 * Quem é este grafo, para o cache.
 *
 * O caminho da raiz não serve: ele vem do arquivo, e dois componentes de
 * origens diferentes podem chegar com o mesmo "ROOT / Component_1". Com a mesma
 * medida e as mesmas sobrescritas, um devolvia as peças do outro.
 */
const graphTokens = new WeakMap<DynamicComponentGraph, string>();
let nextGraphToken = 0;

function graphToken(graph: DynamicComponentGraph): string {
  const existing = graphTokens.get(graph);
  if (existing) return existing;
  const token = `${graph.root.path}#${(nextGraphToken += 1)}`;
  graphTokens.set(graph, token);
  return token;
}

export function evaluateDynamicFor(
  graph: DynamicComponentGraph,
  dimensionsMm: { width: number; height: number; depth: number },
  overrides: DynamicAttributeOverrides = {},
): DynamicEvaluation {
  const request = resolveDynamicRequest(graph, dimensionsMm);
  const overrideKeys = Object.keys(overrides).sort();
  const key = [
    graphToken(graph),
    request.widthMm.toFixed(3),
    request.heightMm.toFixed(3),
    request.depthMm.toFixed(3),
    // Sem isto, uma edição de atributo devolveria a avaliação anterior do cache
    // e o modelo não mexeria.
    overrideKeys.map((entry) => `${entry}=${overrides[entry]}`).join(","),
  ].join("|");
  const cached = evaluationCache.get(key);
  if (cached) return cached;
  const evaluated = evaluateDynamicComponents(withAttributeOverrides(graph, overrides), request);
  const evaluation = withInheritedHidden(withConstructionHelpersHidden(graph, evaluated));
  if (evaluationCache.size >= EVALUATION_CACHE_LIMIT) {
    evaluationCache.delete(evaluationCache.keys().next().value as string);
  }
  evaluationCache.set(key, evaluation);
  return evaluation;
}

/**
 * Oculta os perfis de construção que acompanham peças curvas e oblíquas.
 *
 * Eles são úteis no arquivo de autoria, mas não fazem parte da peça apresentada:
 * assados no GLB, viram tampas quadradas e pontas triangulares acima e abaixo
 * do painel. A identificação é estrutural, pois modelos atuais já chamam a
 * mesma família de `Peça Curva`, `Canto Curvo` e `Base Redonda`, e novos SKPs
 * podem usar outros nomes.
 *
 * Há três assinaturas específicas:
 * - o molde interno `Canco arredondado`, ligado ao seletor `parent!estru`;
 * - um grupo plano (LenZ zero) ao lado da superfície de altura inteira, dentro
 *   de um conjunto que declara `raio`.
 * - o recorte plano de um painel oblíquo: LenZ zero, dentro de `Faces AB`, com
 *   os dois lados calculados por `Len - Prof`. No SketchUp ele serve para
 *   construir o corte diagonal; no GLB assado vira a ponta/quadrado que não
 *   pertence ao móvel.
 *
 * Um `Canco arredondado` na raiz é um tampo legítimo, não um auxiliar. Um grupo
 * plano isolado também não basta para classificar outro tipo de módulo.
 */
function withConstructionHelpersHidden(
  graph: DynamicComponentGraph,
  evaluation: DynamicEvaluation,
): DynamicEvaluation {
  const helperPaths = new Set<string>();

  const visit = (node: DynamicNode): void => {
    const fullHeightSurface = node.children.some((child) => (
      normalizeNodeName(child.name) === "group"
      && child.attributes.lenz?.formula !== undefined
      && /(?:parent|owner)!lenz/i.test(child.attributes.lenz.formula)
    ));
    for (const child of node.children) {
      const childName = normalizeNodeName(child.name);
      const isRoundedTemplate = childName === "canco arredondado"
        && /(?:parent|owner)!estru/i.test(child.attributes.hidden?.formula ?? "");
      const isZeroHeightGuide = normalizeNodeName(child.name) === "group"
        && node.attributes.raio !== undefined
        && fullHeightSurface
        && child.attributes.lenz?.formula === undefined
        && Math.abs(Number(child.attributes.lenz?.value)) < 1e-9;
      const isObliqueCutout = normalizeNodeName(node.name) === "faces ab"
        && child.attributes.lenz?.formula === undefined
        && Math.abs(Number(child.attributes.lenz?.value)) < 1e-9
        && formulaReferences(child.attributes.lenx?.formula, "parent!lenx", "parent!profa")
        && formulaReferences(child.attributes.leny?.formula, "parent!leny", "parent!profb");
      if (isRoundedTemplate || isZeroHeightGuide || isObliqueCutout) helperPaths.add(child.path);
      visit(child);
    }
  };
  visit(graph.root);
  if (!helperPaths.size) return evaluation;

  let changed = false;
  const parts = evaluation.parts.map((part) => {
    if (part.hidden || !helperPaths.has(part.path)) return part;
    changed = true;
    return { ...part, hidden: true };
  });
  return changed ? { ...evaluation, parts } : evaluation;
}

function formulaReferences(formula: string | undefined, ...references: string[]): boolean {
  if (!formula) return false;
  const normalized = formula.replace(/\s/g, "").toLowerCase();
  return references.every((reference) => normalized.includes(reference));
}

function normalizeNodeName(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLocaleLowerCase("pt-BR");
}

/**
 * O que está dentro de um grupo escondido também está escondido.
 *
 * O avaliador resolve `Hidden` nó a nó, e no SketchUp esconder um componente
 * esconde tudo o que ele contém. A diferença aparece nas portas de giro destes
 * arquivos: elas trazem a versão esquerda e a direita no mesmo lugar, e a que
 * não vale sai com `Hidden = IF(OR(parent!ab=2), false, true)`. O grupo saía
 * escondido, mas a frente e a dobradiça dentro dele não — o "Balcão 3" chegava
 * com quatro portas e quatro dobradiças onde o SketchUp mostra três.
 *
 * A herança é por instância, e não por caminho: numa peça replicada por
 * `copies`, esconder uma cópia não pode apagar as irmãs. Por isso o índice de
 * cópia do ancestral tem de abrir o da peça.
 */
function withInheritedHidden(evaluation: DynamicEvaluation): DynamicEvaluation {
  const hiddenByPath = new Map<string, EvaluatedPart[]>();
  for (const part of evaluation.parts) {
    if (!part.hidden) continue;
    const found = hiddenByPath.get(part.path);
    if (found) found.push(part);
    else hiddenByPath.set(part.path, [part]);
  }
  if (!hiddenByPath.size) return evaluation;

  let changed = false;
  const parts = evaluation.parts.map((part) => {
    if (part.hidden) return part;
    const segments = part.path.split(" / ");
    for (let depth = 1; depth < segments.length; depth += 1) {
      const ancestors = hiddenByPath.get(segments.slice(0, depth).join(" / "));
      if (!ancestors?.some((ancestor) => opensCopyPath(ancestor.copyPath, part.copyPath))) continue;
      changed = true;
      return { ...part, hidden: true };
    }
    return part;
  });
  return changed ? { ...evaluation, parts } : evaluation;
}

/** Se a peça descende desta instância, e não de outra cópia do mesmo caminho. */
function opensCopyPath(ancestor: readonly number[], part: readonly number[]): boolean {
  if (ancestor.length > part.length) return false;
  return ancestor.every((index, position) => index === part[position]);
}

/**
 * As sobrescritas de atributo que o elemento guarda, separadas do resto de
 * `instanceOverrides` pelo prefixo.
 */
const ATTRIBUTE_OVERRIDE_PREFIX = "dynamicAttribute:";
/**
 * O que veio de um clique na ferramenta Interagir, e não de uma edição.
 *
 * A distinção existe porque as duas coisas se movem por caminhos diferentes:
 * uma edição de medida redimensiona a peça, e o motor já cuida disso; uma ação
 * só abre e fecha, e é desenhada por cima sem mexer no que a peça é.
 */
const ACTION_OVERRIDE_PREFIX = "dynamicAction:";
/**
 * A compact resting value inferred from the rendered import itself.
 *
 * It is neither a user edit nor the current click state: it defines the closed
 * pose against which later interactions are layered.
 */
const RESTING_OVERRIDE_PREFIX = "dynamicResting:";

function overridesWithPrefix(
  element: SceneElement,
  prefix: string,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(element.instanceOverrides ?? {})) {
    if (!key.startsWith(prefix)) continue;
    if (typeof value === "string" || typeof value === "number") {
      out[key.slice(prefix.length)] = value;
    }
  }
  return out;
}

export function readAttributeOverrides(element: SceneElement): DynamicAttributeOverrides {
  return {
    ...overridesWithPrefix(element, RESTING_OVERRIDE_PREFIX),
    ...overridesWithPrefix(element, ATTRIBUTE_OVERRIDE_PREFIX),
    ...overridesWithPrefix(element, ACTION_OVERRIDE_PREFIX),
  };
}

/** Only explicit design edits, without resting-pose or click state. */
export function readOverridesWithoutActions(element: SceneElement): DynamicAttributeOverrides {
  return overridesWithPrefix(element, ATTRIBUTE_OVERRIDE_PREFIX);
}

/** Explicit edits plus the compact resting pose, before a click opens it. */
export function readRestingOverrides(element: SceneElement): DynamicAttributeOverrides {
  return {
    ...overridesWithPrefix(element, RESTING_OVERRIDE_PREFIX),
    ...overridesWithPrefix(element, ATTRIBUTE_OVERRIDE_PREFIX),
  };
}

/**
 * Evaluates the cabinet itself, without visual interaction state such as an
 * opened door or drawer. Consumers that size, fit or manufacture the module
 * must use this state; interaction motion is layered on afterwards.
 */
export function evaluateDynamicStructureFor(
  graph: DynamicComponentGraph,
  dimensionsMm: { width: number; height: number; depth: number },
  element: SceneElement,
): DynamicEvaluation {
  return evaluateDynamicFor(graph, dimensionsMm, readOverridesWithoutActions(element));
}

/**
 * The state currently shown to the operator, including click-action overrides.
 * It is deliberately separate from the closed structural evaluation: actions
 * may swap mutually exclusive geometry as well as move a door or drawer.
 */
export function evaluateDynamicAppearanceFor(
  graph: DynamicComponentGraph,
  dimensionsMm: { width: number; height: number; depth: number },
  element: SceneElement,
): DynamicEvaluation {
  return evaluateDynamicFor(graph, dimensionsMm, readAttributeOverrides(element));
}

export function hasActionOverrides(element: SceneElement): boolean {
  return Object.keys(element.instanceOverrides ?? {}).some((key) => key.startsWith(ACTION_OVERRIDE_PREFIX));
}

export function attributeOverrideSlot(path: string, attribute: string): string {
  return `${ATTRIBUTE_OVERRIDE_PREFIX}${overrideKey(path, attribute)}`;
}

export function actionOverrideSlot(path: string, attribute: string): string {
  return `${ACTION_OVERRIDE_PREFIX}${overrideKey(path, attribute)}`;
}

export function restingOverrideSlot(path: string, attribute: string): string {
  return `${RESTING_OVERRIDE_PREFIX}${overrideKey(path, attribute)}`;
}

/**
 * Structural values that make an imported click-action rest in its compact
 * state.
 *
 * Most SKP files are baked closed, but a few are saved while a flap or drawer
 * is open. The GLB consequently arrives in that open pose as well. This only
 * proposes the semantic zero/off state declared by the component; the viewport
 * still compares the two rendered envelopes before accepting the proposal, so
 * components whose imported value is already their valid resting state are not
 * inverted merely because their action happens to contain `1, 0`.
 */
export function proposedClosedStructureOverrides(
  graph: DynamicComponentGraph,
): DynamicAttributeOverrides {
  const proposed: Record<string, string | number> = {};
  const evaluated = evaluateDynamicComponents(graph, {});
  const evaluatedByPath = new Map(evaluated.parts.map((part) => [part.path, part]));
  const nodesByName = new Map<string, DynamicNode>();
  const parents = new Map<string, DynamicNode | null>();

  const index = (node: DynamicNode, parent: DynamicNode | null): void => {
    parents.set(node.path, parent);
    nodesByName.set(node.name.toLocaleLowerCase("pt-BR"), node);
    node.children.forEach((child) => index(child, node));
  };
  index(graph.root, null);

  const visit = (node: DynamicNode): void => {
    const action = node.attributes.onclick;
    const script = action?.value;
    const inherited = typeof action?.formula === "string"
      && /^\s*(?:parent|owner)!onclick\s*$/i.test(action.formula);
    if (typeof script === "string" && script.trim() && !inherited) {
      const resolve = (nodeName: string): DynamicNode | null => {
        const normalized = nodeName.toLocaleLowerCase("pt-BR");
        if (!normalized) return node;
        if (normalized === "parent" || normalized === "owner") {
          return parents.get(node.path) ?? null;
        }
        return nodesByName.get(normalized) ?? null;
      };
      const current = (nodeName: string, attribute: string): string | number | null => {
        const target = resolve(nodeName);
        if (!target || target.attributes[attribute] === undefined) return null;
        const staged = proposed[overrideKey(target.path, attribute)];
        if (staged !== undefined) return staged;
        const resolved = evaluatedByPath.get(target.path)?.attributes?.[attribute];
        if (typeof resolved === "string" || typeof resolved === "number") return resolved;
        const declared = target.attributes[attribute]?.value;
        return typeof declared === "string" || typeof declared === "number" ? declared : null;
      };
      const plan = planInteraction(script, current, "closed");
      for (const change of plan.changes) {
        const target = resolve(change.node);
        if (!target || target.attributes[change.attribute] === undefined) continue;
        proposed[overrideKey(target.path, change.attribute)] = change.value;
      }
    }
    node.children.forEach(visit);
  };
  visit(graph.root);
  return proposed;
}

/** Whether a candidate pose is convincingly more compact than the baked one. */
export function isMateriallyMoreCompactPose(
  bakedSize: readonly number[],
  candidateSize: readonly number[],
): boolean {
  if (bakedSize.length < 3 || candidateSize.length < 3) return false;
  if ([...bakedSize, ...candidateSize].some((value) => !Number.isFinite(value) || value <= 0)) {
    return false;
  }
  const tolerance = bakedSize.map((value) => Math.max(value * 0.05, 0.025));
  const grows = candidateSize.some((value, index) => value > bakedSize[index]! + tolerance[index]!);
  if (grows) return false;
  const shrinksAnAxis = candidateSize.some((value, index) => value <= bakedSize[index]! * 0.9);
  const volume = (values: readonly number[]) => values[0]! * values[1]! * values[2]!;
  return shrinksAnAxis && volume(candidateSize) <= volume(bakedSize) * 0.92;
}

/** O movimento visual de uma peça, no referencial do módulo e em polegadas. */
export interface PartMotion {
  matrixInches: number[];
}

/**
 * De quanto cada peça anda por causa das ações — a gaveta que corre, a porta
 * que gira.
 *
 * O motor de componentes resolve o movimento e o entrega em
 * `localTranslationInches`, mas a matriz final que ele devolve mede o
 * deslocamento contra uma âncora tirada do mesmo grafo. Um movimento que vem de
 * atributo, e não de tamanho, aparece nos dois lados dessa subtração e se anula
 * — a gaveta ficava parada por mais que o valor mudasse. Aqui a conta é feita
 * entre dois estados: como está, e como estaria sem as ações. A diferença é o
 * quanto abrir, e ela é aplicada só no desenho.
 */
export function readPartMotions(
  graph: DynamicComponentGraph,
  dimensionsMm: { width: number; height: number; depth: number },
  element: SceneElement,
): Map<string, PartMotion> {
  if (!hasActionOverrides(element)) return new Map();
  return readPartMotionsBetween(
    graph,
    dimensionsMm,
    readRestingOverrides(element),
    readAttributeOverrides(element),
  );
}

/** Motion that rebases a GLB baked open onto the inferred compact rest pose. */
export function readRestingPartMotions(
  graph: DynamicComponentGraph,
  dimensionsMm: { width: number; height: number; depth: number },
  element: SceneElement,
): Map<string, PartMotion> {
  const resting = overridesWithPrefix(element, RESTING_OVERRIDE_PREFIX);
  if (!Object.keys(resting).length) return new Map();
  return readPartMotionsBetween(
    graph,
    dimensionsMm,
    readOverridesWithoutActions(element),
    readRestingOverrides(element),
  );
}

function readPartMotionsBetween(
  graph: DynamicComponentGraph,
  dimensionsMm: { width: number; height: number; depth: number },
  from: DynamicAttributeOverrides,
  to: DynamicAttributeOverrides,
): Map<string, PartMotion> {
  const motions = new Map<string, PartMotion>();
  // Keyed by path rather than copy identity: the leaf mesh asks its ancestors
  // for their movement by this same path.
  const aberto = evaluateDynamicFor(graph, dimensionsMm, to);
  const fechado = evaluateDynamicFor(graph, dimensionsMm, from);
  const fechadoByKey = new Map(fechado.parts.map((part) => [part.key, part]));
  const fechadoByPath = new Map(fechado.parts.map((part) => [part.path, part]));
  const nodesByPath = new Map<string, DynamicNode>();
  indexNodesByPath(graph.root, nodesByPath);
  // O grafo com as sobrescritas já aplicadas, que é onde `ABP` vale 1 depois do
  // clique — o avaliado não o traz, como explica `rotationReader`.
  const giroAberto = rotationReader(aberto.parts, withAttributeOverrides(graph, to));
  const giroFechado = rotationReader(fechado.parts, withAttributeOverrides(graph, from));

  for (const part of aberto.parts) {
    const base = fechadoByKey.get(part.key);
    if (!base) continue;
    const matrixInches = movementOf(
      part,
      base,
      giroAberto(part),
      giroFechado(base),
      parentFrame(fechadoByPath, part.path),
      drawerTravelLimitInches(nodesByPath, fechadoByPath, part.path),
    );
    if (matrixInches) motions.set(part.path, { matrixInches });
  }
  return motions;
}

function indexNodesByPath(node: DynamicNode, into: Map<string, DynamicNode>): void {
  into.set(node.path, node);
  for (const child of node.children) indexNodesByPath(child, into);
}

/**
 * O giro que uma peça declara, resolvido aqui.
 *
 * O avaliador do pacote coloca e dimensiona — `x`, `y`, `z`, `lenx`, `leny`,
 * `lenz` — e não olha para `RotX`, `RotY` e `RotZ`. Mas é por elas que uma porta
 * abre: a do móvel de teste traz `RotY = IF(ABP=1,90,0)`. O pacote é consumido
 * como está, então a conta é refeita deste lado, com a mesma linguagem de
 * fórmula.
 *
 * O contexto vem do que ele já resolveu, com recuo para o que o componente
 * declara: ele só publica o atributo que alguma fórmula sua usou, e `ABP` não é
 * usado por nenhuma — quem o lê são justamente as de giro, que ele ignora. Sem o
 * recuo, a porta ficava fechada porque a referência não resolvia.
 */
function rotationReader(
  parts: readonly EvaluatedPart[],
  graph: DynamicComponentGraph,
): (part: EvaluatedPart) => [number, number, number] {
  const nodesByPath = new Map<string, DynamicNode>();
  indexNodesByPath(graph.root, nodesByPath);
  const byPath = new Map(parts.map((part) => [part.path, part]));
  const byName = new Map<string, string>();
  for (const node of nodesByPath.values()) {
    const name = (node.name || node.definitionName || "").toLowerCase();
    if (name && !byName.has(name)) byName.set(name, node.path);
  }
  const parsed = new Map<string, FormulaNode | null>();

  const tree = (formula: string): FormulaNode | null => {
    let cached = parsed.get(formula);
    if (cached === undefined) {
      try {
        cached = parseFormula(formula);
      } catch {
        cached = null;
      }
      parsed.set(formula, cached);
    }
    return cached;
  };

  /** O valor de um atributo num caminho, avaliando a fórmula se preciso. */
  const valueAt = (
    path: string | undefined,
    name: string,
    seen: Set<string>,
  ): FormulaValue | undefined => {
    if (!path) return undefined;
    const lower = name.toLowerCase();
    const part = byPath.get(path);
    const resolved = part ? attributeValue(part, lower) : undefined;
    if (resolved !== undefined) return resolved;
    const node = nodesByPath.get(path);
    const attribute = node ? attributeOf(node, lower) : undefined;
    if (!attribute) return undefined;
    const declared = attribute.value ?? undefined;
    if (!attribute.formula) return declared;
    const marker = `${path}!${lower}`;
    if (seen.has(marker)) return declared;
    seen.add(marker);
    const parsedTree = tree(attribute.formula);
    if (!parsedTree) return declared;
    try {
      return evaluateFormula(parsedTree, contextAt(path, seen));
    } catch {
      return declared;
    }
  };

  const contextAt = (path: string, seen: Set<string>): FormulaContext => ({
    self: (name) => valueAt(path, name, seen),
    parent: (name) => valueAt(parentPathOf(nodesByPath, path), name, seen),
    named: (owner, name) => valueAt(byName.get(owner.toLowerCase()), name, seen),
  });

  return (part) => {
    const node = nodesByPath.get(part.path);
    if (!node) return [0, 0, 0];
    return ROTATION_ATTRIBUTES.map((key) => {
      const attribute = attributeOf(node, key);
      if (!attribute) return 0;
      if (!attribute.formula) return degreesOf(attribute.value);
      const parsedTree = tree(attribute.formula);
      if (!parsedTree) return degreesOf(attribute.value);
      try {
        return degreesOf(evaluateFormula(parsedTree, contextAt(part.path, new Set())));
      } catch {
        // Uma fórmula de giro que não resolve deixa a peça onde está, como
        // qualquer outra que o avaliador não consegue fechar.
        return degreesOf(attribute.value);
      }
    }) as [number, number, number];
  };
}

const ROTATION_ATTRIBUTES = ["rotx", "roty", "rotz"] as const;

function parentPathOf(
  nodesByPath: ReadonlyMap<string, DynamicNode>,
  path: string,
): string | undefined {
  const segments = path.split(" / ");
  for (let depth = segments.length - 1; depth > 0; depth -= 1) {
    const candidate = segments.slice(0, depth).join(" / ");
    if (nodesByPath.has(candidate)) return candidate;
  }
  return undefined;
}

function attributeOf(node: DynamicNode, name: string): DynamicAttribute | undefined {
  const direct = node.attributes[name];
  if (direct) return direct;
  const found = Object.keys(node.attributes).find((key) => key.toLowerCase() === name);
  return found ? node.attributes[found] : undefined;
}

function attributeValue(part: EvaluatedPart, name: string): FormulaValue | undefined {
  const lower = name.toLowerCase();
  return part.attributes[lower] ?? part.attributes[name];
}

function parentPart(
  parts: ReadonlyMap<string, EvaluatedPart>,
  path: string,
): EvaluatedPart | null {
  const segments = path.split(" / ");
  for (let depth = segments.length - 1; depth > 0; depth -= 1) {
    const parent = parts.get(segments.slice(0, depth).join(" / "));
    if (parent) return parent;
  }
  return null;
}

/**
 * A diferença entre os dois estados, levada ao referencial do módulo.
 *
 * A colocação de uma peça dentro do pai é `T(posição) · R(giro) · S(tamanho)`.
 * Dividir a de agora pela de antes deixa `T(p1) · ΔR · T(−p0)` — o tamanho se
 * cancela, que é o que se quer: abrir uma porta não a redimensiona. Ler ΔR como
 * produto de matrizes, e não como um ângulo, faz valer para qualquer eixo: a
 * porta que gira no vertical e o basculante que gira no horizontal saem da
 * mesma conta.
 */
function movementOf(
  aberto: EvaluatedPart,
  fechado: EvaluatedPart,
  giroAberto: [number, number, number],
  giroFechado: [number, number, number],
  parent: number[] | null,
  drawerTravelLimit: number | null,
): number[] | null {
  const offset: [number, number, number] = [
    aberto.localTranslationInches[0]! - fechado.localTranslationInches[0]!,
    aberto.localTranslationInches[1]! - fechado.localTranslationInches[1]!,
    aberto.localTranslationInches[2]! - fechado.localTranslationInches[2]!,
  ];
  /*
   * Os gaveteiros da base mandam cada peça correr pela profundidade externa do
   * módulo (600 mm), embora a própria gaveta e sua corrediça tenham 550 mm. A
   * diferença deixa a caixa inteiramente fora do móvel, com um vão visível.
   * A profundidade avaliada do contêiner da gaveta é o limite físico do curso e
   * acompanha qualquer medida futura importada com a mesma estrutura.
   */
  if (drawerTravelLimit !== null) {
    const dominantAxis = offset.reduce(
      (largest, value, axis) => Math.abs(value) > Math.abs(offset[largest]!) ? axis : largest,
      0,
    );
    if (Math.abs(offset[dominantAxis]!) > drawerTravelLimit) {
      offset[dominantAxis] = Math.sign(offset[dominantAxis]!) * drawerTravelLimit;
    }
  }
  const spin = multiply4(rotationMatrix(giroAberto), transposeLinear(rotationMatrix(giroFechado)));
  const moved = offset.some((value) => Math.abs(value) > 1e-6);
  const spun = !isIdentityLinear(spin);
  if (!moved && !spun) return null;

  // O deslocamento e o eixo do giro saem no referencial do pai; é a colocação
  // dele que os leva ao do módulo. Usando a da própria peça — que carrega a
  // rotação dela dentro do grupo —, uma gaveta de 600 mm saía 189 mm na
  // diagonal.
  const basis = parent ?? identity4();
  const axes = orthonormal(basis);
  const inModule = multiply4(axes, multiply4(spin, transposeLinear(axes)));
  // O giro é em torno da origem da peça, onde ela está com o móvel fechado.
  const pivot: [number, number, number] = [fechado.worldMatrix[12]!, fechado.worldMatrix[13]!, fechado.worldMatrix[14]!];
  /*
   * `localTranslationInches` is the formula's final coordinate for the current
   * size. A formula such as `x = LenY` has therefore already grown when its
   * containing door grows. Multiplying that difference by the parent's full
   * (possibly non-uniform) scale applies the resize a second time: a 620 mm
   * basculant under a 1.67x parent was displaced as though it were 1,039 mm and
   * its open leaf floated above the cabinet. Only the parent's orientation is
   * needed to carry this already-resolved displacement into module space.
   */
  const [dx, dy, dz] = applyLinear(axes, offset);
  const movement = multiply4(
    translation4([pivot[0] + dx, pivot[1] + dy, pivot[2] + dz]),
    multiply4(inModule, translation4([-pivot[0], -pivot[1], -pivot[2]])),
  );
  return movement;
}

/** Profundidade da gaveta que contém esta peça, em polegadas. */
function drawerTravelLimitInches(
  nodesByPath: ReadonlyMap<string, DynamicNode>,
  partsByPath: ReadonlyMap<string, EvaluatedPart>,
  path: string,
): number | null {
  const segments = path.split(" / ");
  const actionOwnerDepthsMm: number[] = [];
  const namedDrawerDepthsMm: number[] = [];
  for (let depth = segments.length; depth > 0; depth -= 1) {
    const candidate = segments.slice(0, depth).join(" / ");
    const node = nodesByPath.get(candidate);
    const part = partsByPath.get(candidate);
    if (!node || !part) continue;
    const identity = normalizeNodeName(`${node.name} ${node.definitionName}`);
    const ownsDrawerAction = attributeOf(node, "ab_gav") !== undefined
      || attributeOf(node, "abgav") !== undefined;
    const isNamedDrawer = /(?:^|\s)(?:gaveta|drawer)(?:\s|$)/.test(identity);
    if (!ownsDrawerAction && !isNamedDrawer) continue;
    // A medida assada é a dimensão física que a malha da gaveta realmente
    // ocupa. O componente que declara AB_GAV é a gaveta e seu LenY é a
    // profundidade. Um descendente chamado "corpo gaveta" troca os eixos para
    // construir as laterais: o LenY dele é a *altura*. Usá-lo como limite fazia
    // o MDF andar só 123/306 mm enquanto frente e corrediças iam 550 mm.
    const bucket = ownsDrawerAction ? actionOwnerDepthsMm : namedDrawerDepthsMm;
    bucket.push(...[part.baseSizeMm.y, part.sizeMm.y]
      .filter((value) => Number.isFinite(value) && value > 1e-6));
  }
  // Arquivos antigos sem AB_GAV explícito ainda têm a identificação nominal.
  // Quando o dono da ação existe, ele é a fonte autoritativa e os nomes de
  // grupos internos não podem reduzir o curso.
  const drawerDepthsMm = actionOwnerDepthsMm.length
    ? actionOwnerDepthsMm
    : namedDrawerDepthsMm;
  const depthInches = Math.min(...drawerDepthsMm) / MILLIMETERS_PER_INCH;
  return Number.isFinite(depthInches) && depthInches > 1e-6 ? depthInches : null;
}

/** A colocação avaliada do grupo que contém este caminho. */
function parentFrame(
  parts: ReadonlyMap<string, EvaluatedPart>,
  path: string,
): number[] | null {
  const segments = path.split(" / ");
  for (let depth = segments.length - 1; depth > 0; depth -= 1) {
    const parent = parts.get(segments.slice(0, depth).join(" / "));
    if (parent) return linearOf(parent.worldMatrix);
  }
  return null;
}

/** O giro em torno dos três eixos, em graus, como matriz. */
function rotationMatrix([graus, grausY, grausZ]: [number, number, number]): number[] {
  const x = (graus * Math.PI) / 180;
  const y = (grausY * Math.PI) / 180;
  const z = (grausZ * Math.PI) / 180;
  const [cx, sx] = [Math.cos(x), Math.sin(x)];
  const [cy, sy] = [Math.cos(y), Math.sin(y)];
  const [cz, sz] = [Math.cos(z), Math.sin(z)];
  const rx = [1, 0, 0, 0, 0, cx, sx, 0, 0, -sx, cx, 0, 0, 0, 0, 1];
  const ry = [cy, 0, -sy, 0, 0, 1, 0, 0, sy, 0, cy, 0, 0, 0, 0, 1];
  const rz = [cz, sz, 0, 0, -sz, cz, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
  /*
   * Cada eixo gira sobre o que o anterior deixou, e não sobre os eixos do pai.
   * A porta do móvel de teste é desenhada deitada e endireitada por
   * `RotX = -90`; é depois disso que `RotY = 90` a abre, e por isso ela gira no
   * eixo vertical, como uma porta de dobradiça. Lendo os três no referencial do
   * pai, o mesmo 90 tombava a porta para frente.
   */
  return multiply4(rx, multiply4(ry, rz));
}

function linearOf(matrix: number[]): number[] {
  return [
    matrix[0]!, matrix[1]!, matrix[2]!, 0,
    matrix[4]!, matrix[5]!, matrix[6]!, 0,
    matrix[8]!, matrix[9]!, matrix[10]!, 0,
    0, 0, 0, 1,
  ];
}

/**
 * A mesma orientação sem o tamanho embutido.
 *
 * O giro volta do referencial do módulo pela transposta da base, e transposta
 * só é inversa quando os eixos têm comprimento um. Num grupo esticado, sem
 * normalizar, a porta girava torta.
 */
function orthonormal(basis: number[]): number[] {
  const result = identity4();
  for (let axis = 0; axis < 3; axis += 1) {
    const column = axis * 4;
    const length = Math.hypot(basis[column]!, basis[column + 1]!, basis[column + 2]!) || 1;
    result[column] = basis[column]! / length;
    result[column + 1] = basis[column + 1]! / length;
    result[column + 2] = basis[column + 2]! / length;
  }
  return result;
}

function transposeLinear(matrix: number[]): number[] {
  return [
    matrix[0]!, matrix[4]!, matrix[8]!, 0,
    matrix[1]!, matrix[5]!, matrix[9]!, 0,
    matrix[2]!, matrix[6]!, matrix[10]!, 0,
    0, 0, 0, 1,
  ];
}

function translation4(offset: [number, number, number]): number[] {
  const matrix = identity4();
  matrix[12] = offset[0];
  matrix[13] = offset[1];
  matrix[14] = offset[2];
  return matrix;
}

function applyLinear(matrix: number[], vector: [number, number, number]): [number, number, number] {
  const [x, y, z] = vector;
  return [
    matrix[0]! * x + matrix[4]! * y + matrix[8]! * z,
    matrix[1]! * x + matrix[5]! * y + matrix[9]! * z,
    matrix[2]! * x + matrix[6]! * y + matrix[10]! * z,
  ];
}

function isIdentityLinear(matrix: number[]): boolean {
  const identity = identity4();
  for (let index = 0; index < 11; index += 1) {
    if (Math.abs(matrix[index]! - identity[index]!) > 1e-9) return false;
  }
  return true;
}

/**
 * O movimento que vale para esta peça: o dos grupos acima dela e o dela.
 *
 * Quem se move é o grupo — "corpo gaveta" — e quem tem geometria são as peças
 * dentro dele. O laço de desenho pula todo caminho sem malha, então o grupo
 * andava sozinho, sem nada preso nele, e a gaveta ficava parada.
 *
 * E os movimentos se somam, em vez de um substituir o outro: a frente da gaveta
 * corre com a gaveta e ainda tem folga própria. Ficando só com o dela, a frente
 * andava 200 mm enquanto as laterais andavam 600 — a gaveta se desmontava no ar.
 */
export function motionFor(
  motions: ReadonlyMap<string, PartMotion>,
  path: string,
): PartMotion | undefined {
  if (!motions.size) return undefined;
  const segments = path.split(" / ");
  let composed: number[] | null = null;
  for (let depth = 1; depth <= segments.length; depth += 1) {
    const step = motions.get(segments.slice(0, depth).join(" / "));
    if (!step) continue;
    composed = composed ? multiply4(composed, step.matrixInches) : step.matrixInches;
  }
  return composed ? { matrixInches: composed } : undefined;
}

/**
 * O movimento nos eixos do desenho, pronto para multiplicar a colocação da peça.
 *
 * A conta toda é feita no referencial do módulo, em polegadas; aqui ela só troca
 * de eixos — a mesma troca que qualquer outra colocação importada faz. Por estar
 * no referencial do módulo, o mesmo movimento serve para o grupo que abre e para
 * cada peça pendurada nele.
 */
export function motionMatrix(motion: PartMotion): number[] {
  return skpToGlb(motion.matrixInches);
}

function degreesOf(value: unknown): number {
  if (typeof value === "number") return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Bounding box of one part's baked geometry, in the loaded model's own axes. */
export interface DynamicPartBounds {
  minimum: [number, number, number];
  maximum: [number, number, number];
  /** Physical span along the component's untouched local axes, in metres. */
  localSize?: [number, number, number];
}

/**
 * What each part occupies as imported, per module asset.
 *
 * Measured in the viewport the first time a module loads, then kept here so the
 * store can answer "how small can this actually get?" without a scene. Without
 * it the only available answer is the outermost component's declared length,
 * and that keeps shrinking long after the panels and hardware inside have run
 * out of room — which is what left parts hanging outside the selection box.
 */
const partBoundsByAsset = new Map<string, Map<string, DynamicPartBounds>>();

export function registerDynamicPartBounds(
  assetId: string,
  bounds: Map<string, DynamicPartBounds>,
): void {
  if (bounds.size) partBoundsByAsset.set(assetId, bounds);
}

export function readDynamicPartBounds(
  element: SceneElement,
): Map<string, DynamicPartBounds> | null {
  const assetId = dynamicAssetIdFor(element);
  return (assetId && partBoundsByAsset.get(assetId)) || null;
}

/** The bounding size the module reaches for a requested one, in millimetres. */
export function resolveDynamicAchievableDimensions(
  graph: DynamicComponentGraph,
  dimensionsMm: { width: number; height: number; depth: number },
  bounds?: Map<string, DynamicPartBounds> | null,
): { width: number; height: number; depth: number } {
  // Measuring the parts and feeding that back as the new box was tried and
  // removed: the box takes the measured size, the module is rebuilt at that
  // size and measures differently again, and the two chase each other. Four
  // passes did not settle it — a cabinet asked to be 400 mm tall came back
  // 195 m wide, then 50 km. The declared length is stable, and being wrong by
  // a panel's thickness at the very bottom of the range is a far smaller
  // problem than a module that runs away.
  void bounds;
  return resolveDeclaredDimensions(graph, evaluateDynamicFor(graph, dimensionsMm));
}

/** Fallback when a module's parts have not been measured yet. */
function resolveDeclaredDimensions(
  graph: DynamicComponentGraph,
  evaluation: DynamicEvaluation,
): { width: number; height: number; depth: number } {
  const declared = declaredLengthsMm(graph);
  const overhang = {
    width: graph.baseDimensionsMm.width - declared.width,
    height: graph.baseDimensionsMm.height - declared.height,
    depth: graph.baseDimensionsMm.depth - declared.depth,
  };
  return {
    width: evaluation.dimensionsMm.width + overhang.width,
    height: evaluation.dimensionsMm.height + overhang.height,
    depth: evaluation.dimensionsMm.depth + overhang.depth,
  };
}


/** LenX/LenY/LenZ of the outermost component as imported, in millimetres. */
const declaredLengthsByGraph = new WeakMap<DynamicComponentGraph, {
  width: number;
  height: number;
  depth: number;
}>();

export function declaredLengthsMm(graph: DynamicComponentGraph): {
  width: number;
  height: number;
  depth: number;
} {
  const cached = declaredLengthsByGraph.get(graph);
  if (cached) return cached;

  /*
   * O valor gravado junto da fórmula pode ser obsoleto. Nas cubas esculpidas,
   * LenZ ainda traz 220 mm (a caixa com a cuba abaixo), mas `LenZ = esp` resolve
   * 20 mm (só o tampo). Se o valor velho for tratado como comprimento
   * declarado, perde-se o balanço de 200 mm no primeiro redimensionamento e o
   * editor comprime/esconde a cuba. Avaliar a raiz no tamanho importado lê a
   * mesma verdade que o SketchUp usa, sem exigir reimportação.
   */
  const baseEvaluation = evaluateDynamicComponents(graph, {});
  const failedRootAttributes = new Set(
    baseEvaluation.problems
      .filter((problem) => problem.path === graph.root.path)
      .map((problem) => problem.attribute.toLowerCase()),
  );
  const read = (
    name: "lenx" | "leny" | "lenz",
    evaluatedValue: number,
    fallback: number,
  ): number => {
    const formula = graph.root.attributes[name]?.formula;
    const formulaResolved = formula !== undefined && !failedRootAttributes.has(name);
    if (Number.isFinite(evaluatedValue) && (evaluatedValue > 0 || formulaResolved)) {
      return Math.max(0, evaluatedValue);
    }
    const stored = graph.root.attributes[name]?.value;
    const numeric = typeof stored === "number" ? stored : Number(stored);
    return Number.isFinite(numeric) && numeric > 0 ? numeric * 25.4 : fallback;
  };
  const declared = {
    width: read("lenx", baseEvaluation.dimensionsMm.width, graph.baseDimensionsMm.width),
    height: read("lenz", baseEvaluation.dimensionsMm.height, graph.baseDimensionsMm.height),
    depth: read("leny", baseEvaluation.dimensionsMm.depth, graph.baseDimensionsMm.depth),
  };
  declaredLengthsByGraph.set(graph, declared);
  return declared;
}

/**
 * Which asset holds a module's formula graph.
 *
 * A module placed before this feature existed has nothing in its extensions, and
 * rewriting every saved project to add it would touch the customer's data for a
 * field the catalogue already knows. So the catalogue answers instead, and the
 * element's own extension only wins when it has one.
 */
const dynamicAssetByCatalogItem = new Map<string, string>();

export function registerDynamicCatalog(items: readonly CatalogItem[]): void {
  for (const item of items) {
    const assetId = item.engineering?.dynamicAssetId;
    if (!assetId || !item.stableItemId) continue;
    dynamicAssetByCatalogItem.set(item.stableItemId, assetId);
  }
}

export function dynamicAssetIdFor(element: SceneElement): string | null {
  const declared = element.extensions?.engineeringDynamicAssetId;
  if (typeof declared === "string" && declared) return declared;
  const itemId = element.catalogRef?.itemId;
  return (itemId && dynamicAssetByCatalogItem.get(itemId)) || null;
}

/**
 * Graphs are fetched once per asset and kept, because both the viewport and the
 * store need them: the viewport to place the parts, the store to know how far a
 * typed dimension can actually go.
 */
const graphRequests = new Map<string, Promise<DynamicComponentGraph | null>>();
const graphByAsset = new Map<string, DynamicComponentGraph>();

export function loadDynamicGraph(
  assetId: string,
  fetchGraph: (assetId: string) => Promise<DynamicComponentGraph>,
): Promise<DynamicComponentGraph | null> {
  const cached = graphRequests.get(assetId);
  if (cached) return cached;
  const pending = fetchGraph(assetId)
    .then((graph) => {
      graphByAsset.set(assetId, graph);
      return graph;
    })
    .catch(() => null);
  graphRequests.set(assetId, pending);
  return pending;
}

/** The graph already in memory for an element, or null while it is still loading. */
export function readLoadedDynamicGraph(element: SceneElement): DynamicComponentGraph | null {
  const assetId = dynamicAssetIdFor(element);
  return (assetId && graphByAsset.get(assetId)) || null;
}

/** True when the matrix is close enough to the identity to skip writing it. */
export function isIdentity(matrix: number[], tolerance = 1e-9): boolean {
  const reference = identity4();
  return matrix.every((value, index) => Math.abs(value - reference[index]!) <= tolerance);
}
